/**
 * Custom element that displays product recommendations using Shopify Section Rendering.
 * 
 * Instead of building HTML on the client, this component fetches server-rendered
 * product cards from Shopify sections, which automatically handles:
 * - Price formatting and currency conversion
 * - Multi-language translations
 * - Consistent styling with theme
 * - Security (no client-side HTML generation)
 * 
 * @element recommended-products-search
 * @attr {string} data-product-id - Product ID for recommendations
 * @attr {string} data-search-query - Search query for recommendations
 * @attr {number} data-limit - Maximum number of products to display (default: 4)
 * @attr {string} data-intent - Recommendation intent: 'related' or 'complementary' (default: 'related')
 * @attr {string} data-section-id - Section identifier
 * 
 * @fires recommendations:loaded - Dispatched when products are successfully loaded
 */
if (!customElements.get('recommended-products-search')) {
  customElements.define(
    'recommended-products-search',
    class RecommendedProductsSearch extends HTMLElement {
      /**
       * Initializes the component and sets up data attributes
       */
      constructor() {
        super();
        this._requestId = 0;
        this.productId = this.dataset.productId;
        this.searchQuery = this.dataset.searchQuery;
        this.limit = parseInt(this.dataset.limit) || 4;
        this.intent = this.dataset.intent || 'related';
        this.sectionId = this.dataset.sectionId;
      }

      /**
       * Lifecycle callback triggered when element is added to DOM
       * Initiates the recommendation loading process
       */
      connectedCallback() {
        this.loadRecommendations();
      }

      /**
       * Loads product recommendations using Shopify Section Rendering
       * Falls back to empty state if no products are found
       * @async
       */
      async loadRecommendations() {
        const requestId = ++this._requestId;
        
        try {
          this.showLoading();

          let html = '';

          // Use Shopify Section Rendering based on data source
          if (this.productId) {
            html = await this.fetchRecommendationsSection();
          } else if (this.searchQuery) {
            html = await this.fetchSearchRecommendationsSection();
          }

          // Ignore stale responses
          if (requestId !== this._requestId) return;

          if (html && html.trim().length > 0) {
            this.renderServerHTML(html);
          } else {
            this.showEmptyState();
          }
        } catch (error) {
          // Ignore stale responses
          if (requestId !== this._requestId) return;
          
          console.error('[RecommendedProducts] Error loading recommendations:', error);
          this.showError();
        }
      }

      /**
       * Fetches recommendations using Shopify's Section Rendering API
       * Shopify handles all price formatting, currencies, and translations
       * @async
       * @returns {Promise<string>} Server-rendered HTML
       */
      async fetchRecommendationsSection() {
        const url = `${window.Shopify.routes.root}recommendations/products?section_id=recommended-products-cards&product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const html = await response.text();
        
        // Parse and extract the results div
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const results = doc.querySelector('[data-recommendations-results]');
        
        return results ? results.innerHTML : '';
      }

      /**
       * Fetches search-based recommendations using Section Rendering
       * First gets product list from Predictive Search API, then fetches
       * server-rendered cards for each product
       * @async
       * @returns {Promise<string>} Server-rendered HTML
       */
      async fetchSearchRecommendationsSection() {
        // Step 1: Get product list from Predictive Search API
        const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(this.searchQuery)}&resources[type]=product&resources[limit]=10`;
        
        const searchResponse = await fetch(searchUrl);
        
        if (!searchResponse.ok) {
          throw new Error(`HTTP error! status: ${searchResponse.status}`);
        }
        
        const searchData = await searchResponse.json();
        
        // Extract products from response
        let products = [];
        
        if (searchData.resources?.results?.products) {
          products = searchData.resources.results.products;
        } else if (searchData.products) {
          products = searchData.products;
        }
        
        if (products.length === 0) {
          return '';
        }
        
        // Shuffle using Fisher-Yates and limit products
        const shuffled = [...products];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const limited = shuffled.slice(0, this.limit);
        
        // Step 2: Fetch server-rendered cards for each product
        const cardPromises = limited.map(product => 
          fetch(`${window.Shopify.routes.root}products/${product.handle}?section_id=search-recommendations-cards`)
            .then(response => response.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const card = doc.querySelector('.recommended-product-card');
              return card ? card.outerHTML : '';
            })
            .catch(error => {
              console.error(`[RecommendedProducts] Error fetching card for ${product.handle}:`, error);
              return '';
            })
        );
        
        const cards = await Promise.all(cardPromises);
        return cards.filter(card => card.length > 0).join('');
      }

      /**
       * Renders server-generated HTML into the container
       * @param {string} html - Server-rendered HTML from Shopify
       */
      renderServerHTML(html) {
        const container = this.querySelector('[data-products-container]');
        if (!container) return;

        // Clear container safely
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }

        // Insert server-rendered HTML (safe because it comes from Shopify)
        container.innerHTML = html;

        this.hideLoading();
        this.classList.add('loaded');

        // Count products for event
        const productCards = container.querySelectorAll('.recommended-product-card');

        // Dispatch event to notify that products have been loaded
        this.dispatchEvent(new CustomEvent('recommendations:loaded', {
          bubbles: true,
          detail: { productsCount: productCards.length }
        }));
      }

      /**
       * Displays loading spinner in the products container
       */
      showLoading() {
        const container = this.querySelector('[data-products-container]');
        if (container) {
          // Clear container safely
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
          
          const loadingDiv = document.createElement('div');
          loadingDiv.className = 'recommended-products__loading';
          
          const spinner = document.createElement('div');
          spinner.className = 'spinner';
          spinner.setAttribute('role', 'status');
          
          const srText = document.createElement('span');
          srText.className = 'sr-only';
          srText.textContent = 'Loading recommendations...';
          
          spinner.appendChild(srText);
          loadingDiv.appendChild(spinner);
          container.appendChild(loadingDiv);
        }
      }

      /**
       * Removes loading spinner from the DOM
       */
      hideLoading() {
        const loading = this.querySelector('.recommended-products__loading');
        if (loading) {
          loading.remove();
        }
      }

      /**
       * Displays empty state message when no products are found
       */
      showEmptyState() {
        const container = this.querySelector('[data-products-container]');
        if (container) {
          // Clear container safely
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
          
          const emptyDiv = document.createElement('div');
          emptyDiv.className = 'recommended-products__empty';
          
          const message = document.createElement('p');
          message.textContent = 'No recommendations are available at this time.';
          
          emptyDiv.appendChild(message);
          container.appendChild(emptyDiv);
        }
        this.hideLoading();
      }

      /**
       * Displays error message when recommendations fail to load
       */
      showError() {
        const container = this.querySelector('[data-products-container]');
        if (container) {
          // Clear container safely
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
          
          const errorDiv = document.createElement('div');
          errorDiv.className = 'recommended-products__error';
          
          const message = document.createElement('p');
          message.textContent = 'The recommendations could not be loaded. Please try again later.';
          
          errorDiv.appendChild(message);
          container.appendChild(errorDiv);
        }
        this.hideLoading();
      }

      /**
       * Public method to reload recommendations with optional new parameters
       * @param {string|null} newProductId - New product ID to fetch recommendations for
       * @param {string|null} newSearchQuery - New search query to fetch recommendations for
       */
      refresh(newProductId = null, newSearchQuery = null) {
        if (newProductId !== null) {
          this.productId = newProductId;
          this.dataset.productId = newProductId;
        }
        if (newSearchQuery !== null) {
          this.searchQuery = newSearchQuery;
          this.dataset.searchQuery = newSearchQuery;
        }
        this.loadRecommendations();
      }
    }
  );
}
